import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { navigateInShell, navigateRoot } from '../../../shell/navigation/rootNavigation';
import { consumeReportsNavigationIntent } from '../reportsNavigationIntent';
import { getReportsDateRangeLabel, type ReportsCustomRange } from '../reportsScreen.utils';
import type { ReportsOperacionAnchor } from '../../application/reportsNavigationIntent';
import type { DateRangePreset, ReportsSection } from '../reportsScreen.constants';

export function useReportsNavigationPane() {
  const navigation = useNavigation<any>();
  const isFocused = useIsFocused();

  const [activeSection, setActiveSection] = useState<ReportsSection>('resumen');
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('today');
  const [customRangeFrom, setCustomRangeFrom] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [customRangeTo, setCustomRangeTo] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });

  const customRangeParam = useMemo((): ReportsCustomRange | null => {
    if (dateRangePreset !== 'custom') return null;
    return { from: customRangeFrom, to: customRangeTo };
  }, [dateRangePreset, customRangeFrom, customRangeTo]);

  const isResumenSection = activeSection === 'resumen';
  const isVentasSection = activeSection === 'ventas';
  const isPersonalSection = activeSection === 'personal';
  const isOperacionSection = activeSection === 'operacion';
  const isAgendaSection = activeSection === 'agenda';
  const shouldLoadIntelligence = isFocused && isOperacionSection;
  const shouldLoadOperacionDashboard = isFocused && (isResumenSection || isVentasSection || isOperacionSection);
  const shouldLoadPersonalDashboard = isFocused && isPersonalSection;
  const shouldLoadAgendaDashboard = isFocused && isAgendaSection;
  const shouldLoadTreasurySummary =
    isFocused && (isResumenSection || isOperacionSection);

  const [pendingOperacionAnchor, setPendingOperacionAnchor] =
    useState<ReportsOperacionAnchor | null>(null);

  const dateRangeLabel = useMemo(
    () =>
      getReportsDateRangeLabel(
        dateRangePreset,
        dateRangePreset === 'custom' ? { from: customRangeFrom, to: customRangeTo } : null
      ),
    [dateRangePreset, customRangeFrom, customRangeTo]
  );

  const handleChangeSection = useCallback((section: ReportsSection) => {
    setActiveSection(section);
  }, []);

  const handleOpenBranchSelector = useCallback(() => {
    navigateRoot(navigation, 'BranchSelector');
  }, [navigation]);

  const handleOpenMercadoPagoSettings = useCallback(() => {
    navigateInShell(navigation, 'MercadoPagoBranchSettings');
  }, [navigation]);

  const handleOpenCashWorkspace = useCallback(() => {
    navigateInShell(navigation, 'CashManagement');
  }, [navigation]);

  useEffect(() => {
    if (!isFocused) return;
    const intent = consumeReportsNavigationIntent();
    if (!intent) return;
    if (intent.section) setActiveSection(intent.section as ReportsSection);
    if (intent.operacionAnchor) setPendingOperacionAnchor(intent.operacionAnchor);
  }, [isFocused]);

  const consumeOperacionAnchor = useCallback((): ReportsOperacionAnchor | null => {
    const anchor = pendingOperacionAnchor;
    if (anchor) setPendingOperacionAnchor(null);
    return anchor;
  }, [pendingOperacionAnchor]);

  return {
    navigation,
    isFocused,
    activeSection,
    setActiveSection: handleChangeSection,
    isResumenSection,
    isVentasSection,
    isPersonalSection,
    isOperacionSection,
    isAgendaSection,
    shouldLoadIntelligence,
    shouldLoadOperacionDashboard,
    shouldLoadPersonalDashboard,
    shouldLoadAgendaDashboard,
    shouldLoadTreasurySummary,
    pendingOperacionAnchor,
    consumeOperacionAnchor,
    dateRangePreset,
    setDateRangePreset,
    customRangeFrom,
    setCustomRangeFrom,
    customRangeTo,
    setCustomRangeTo,
    customRangeParam,
    dateRangeLabel,
    handleOpenBranchSelector,
    handleOpenMercadoPagoSettings,
    handleOpenCashWorkspace,
  };
}
